// The one place this app asks the kernel "what is waiting for me?" — E12d/R2 + R1.
//
// ⭐ IT IS A HOOK AROUND A **WRITE**, AND THAT IS NOT A MISTAKE. `POST /me/engagement/sync`
// issues any 30/60/90-day survey that has fallen due: the kernel has no scheduler, by owner
// ruling, so the act of the app checking in is what makes an overdue milestone exist. A GET
// could not do that, and a screen that never called this would show a patient no surveys for
// ever.
//
// ⛔ THIS IS NOT A PUSH SUBSTITUTE AND MUST NOT BECOME A POLLER. Ruling R1 is "in-app only for
// now, push later": the patient finds out about a clinician's reply when they open the app.
// This fires on mount and when the tab becomes visible again — the same two moments
// `useEngagementFeatures` uses — and on nothing else. ⚠️ Do not add a `setInterval` here.
//
// ⚠️ FAILURE IS "NOTHING TO SAY", NOT A BANNER. An unreachable kernel must not put a red badge
// on a menu row; `NO_ENGAGEMENT_SYNC` has every count at zero and `checked_in_today: true`, so
// a broken call is silent rather than nagging.
//
// ⛔ SIGNED OUT MEANS NO CALL. Ionic keeps a page mounted after the user leaves it, so the
// screen someone signed out from keeps running this hook on the sign-in and code screens. Each
// call 401'd, the shared interceptor's refresh 401'd too (web: no cookie), and the resulting
// auth:logout wiped the in-flight login and threw the user off the code screen (prod
// 2026-10-27). The hook answers "off" without asking until someone is signed in, and asks the
// moment they are.

import { useCallback, useEffect, useState } from "react";
import useAuth from "./useAuth";
import {
  EngagementSync,
  NO_ENGAGEMENT_SYNC,
  syncEngagement,
} from "../services/engagement";

export interface UseEngagementSync {
  sync: EngagementSync;
  /** True until the first answer arrives — badges render nothing rather than a zero. */
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export const useEngagementSync = (enabled = true): UseEngagementSync => {
  const [sync, setSync] = useState<EngagementSync>(NO_ENGAGEMENT_SYNC);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  const refresh = useCallback(async () => {
    if (!enabled || !isAuthenticated) {
      setSync(NO_ENGAGEMENT_SYNC);
      setIsLoading(false);
      return;
    }
    try {
      setSync(await syncEngagement());
    } catch (error) {
      // Quiet, not loud — see the header.
      console.error("engagement: sync could not be read", error);
      setSync(NO_ENGAGEMENT_SYNC);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, isAuthenticated]);

  useEffect(() => {
    void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return { sync, isLoading, refresh };
};

export default useEngagementSync;
